// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/seeds/dashboard-v2/auth-flows/register-from-email.yaml
// Generator: tools/v2-gen/v2genauth.py
// Regenerate via: make v2-generate-auth-pages
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-auth-flow-spec.md §5
// for override blocks (imports / helpers / validation / success-action /
// state-render).

import { useState, type FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "../../api/client";
import { AuthLayout } from "./AuthLayout";
import { inputStyle, labelStyle, errorStyle, submitStyle, ctaLinkStyle } from "./styles";
// override-block:imports BEGIN
// override-block:imports END

function decodeJwtPayloadClaim(token: string, claim: string): string | null {
  // Display-only base64 decode of a JWT's payload section. The backend
  // re-validates the signed token on every protected call; we never use
  // the decoded value as an authentication claim here.
  if (!token || token.split(".").length !== 3) return null;
  try {
    const payload = token.split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const v = (json as Record<string, unknown>)?.[claim];
    return typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

// override-block:helpers BEGIN
// override-block:helpers END

type State = "form" | "submitting" | "success" | "error";

export default function RegisterFromEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const email = decodeJwtPayloadClaim(token, "sub") ?? "";
  const [state, setState] = useState<State>("form");
  const [error, setError] = useState<string>("");
  const [fullName, setFullName] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing registration token. Use the link from your invitation email.");
      setState("error");
      return;
    }
    if (fullName.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }
    if (pwd.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (confirm !== pwd) {
      setError("Passwords do not match.");
      return;
    }
    // override-block:validation BEGIN
    // override-block:validation END
    setState("submitting");
    try {
      await apiClient().post("/auth/register-from-email", { token: token, full_name: fullName.trim(), password: pwd });
      // override-block:success-action BEGIN
      // override-block:success-action END
      setState("success");
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Failed to complete registration. Please try again.");
      setState("form");
    }
  }

  // override-block:state-render BEGIN
  // override-block:state-render END

  if (state === "success") {
    return (
      <AuthLayout pageId="register-from-email" title={"Account created"}>
        <p data-role="body" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          Your account is ready. Sign in to continue.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Continue to sign-in
        </Link>
      </AuthLayout>
    );
  }

  if (state === "error") {
    return (
      <AuthLayout pageId="register-from-email" title={"Registration link unavailable"}>
        <div data-role="error" style={errorStyle}>{error}</div>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Go to sign-in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout pageId="register-from-email" title={"Complete your registration"} subtitle={((email)) ? `Signing up as ${email}` : undefined}>
      <form onSubmit={onSubmit} data-form="register-from-email">
        <label style={labelStyle}>
          Full name
          <input type="text" autoComplete="name" required minLength={2} value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={state === "submitting"} data-field="full-name" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Create a password
          <input type="password" autoComplete="new-password" required minLength={12} value={pwd} onChange={(e) => setPwd(e.target.value)} disabled={state === "submitting"} data-field="new-password" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Confirm password
          <input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={state === "submitting"} data-field="confirm-password" style={inputStyle} />
        </label>
        {error && (<div data-role="error" style={errorStyle}>{error}</div>)}
        <button type="submit" disabled={state === "submitting"} data-action="submit" style={submitStyle(state === "submitting")}>
          {state === "submitting" ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
